'use client';

import * as DS from '@aurorasolar/ds';
import { ReactNode } from 'react';

import { LocationSubmit, type LocationSubmitProps } from '@/app/components';
import { EventData, TrackingEvent } from '@/helpers/tracking';

import { ButtonLink } from './ButtonLink';
import { LegalLinks } from './LegalLinks';
import { Subscribe } from './Subscribe';
import { TrustIndicators } from './TrustIndicators';

type Event = { data?: EventData; name: TrackingEvent };

type LinkType = {
  event: Event;
  label: string;
  path: string;
  variant?: 'cta' | 'primary' | 'secondary' | 'text';
};

type LinkProps = {
  mainLink: LinkType;
  secondaryLink?: LinkType;
};
type Props = {
  action?: 'email' | 'link' | 'location';
  bg?: string;
  children?: ReactNode;
  linkProps?: LinkProps;
  locationProps?: LocationSubmitProps;
  variant?: 'cta' | 'primary' | 'secondary' | 'text';
};

export const CtaSection = ({
  action = 'location',
  bg = 'uiEmptyViewBg',
  children,
  linkProps,
  locationProps,
  variant,
}: Props) => {
  return (
    <DS.DarkModeProvider>
      <DS.Flex bg={bg} px={{ _: 6, lg: 10 }} py={{ _: 7, lg: 8 }}>
        <DS.Flex
          alignItems="flex-start"
          flexDirection={{ _: 'column', lg: 'row' }}
          gap={{ _: 4, lg: 7 }}
          justifyContent={children ? 'space-between' : 'center'}
          maxW={DS.BorealisTheme.screens.xl}
          mx="auto"
        >
          {children ? (
            <DS.Text flex={{ _: 1, lg: '2 1 0' }} text={{ _: 'h3', lg: 'h2', xl: 'h1' }}>
              {children}
            </DS.Text>
          ) : null}
          <DS.Flex flex={{ _: 1, lg: '3 1 0' }} gap={2} maxW={{ _: '100%', lg: '600px' }}>
            {action === 'location' && locationProps ? (
              <>
                <LocationSubmit variant="cta" {...locationProps} />
                <TrustIndicators />
              </>
            ) : action === 'email' ? (
              <>
                <DS.LightModeProvider>
                  <Subscribe buttonText="Subscribe" icon={DS.IconArrowRight} variant={variant} />
                </DS.LightModeProvider>
                <LegalLinks />
              </>
            ) : action === 'link' && linkProps ? (
              <DS.Flex
                alignItems={{ _: 'flex-start', md: 'center' }}
                flexDirection={{ _: 'column', md: 'row' }}
                gap={{ _: 3, md: 4 }}
              >
                <ButtonLink
                  event={linkProps.mainLink.event}
                  path={linkProps.mainLink.path}
                  variant={linkProps.mainLink.variant || 'cta'}
                >
                  {linkProps.mainLink.label}
                </ButtonLink>
                {linkProps.secondaryLink ? (
                  <ButtonLink
                    event={linkProps.secondaryLink.event}
                    path={linkProps.secondaryLink.path}
                    variant={linkProps.secondaryLink.variant || 'text'}
                  >
                    {linkProps.secondaryLink.label}
                  </ButtonLink>
                ) : null}
              </DS.Flex>
            ) : null}
          </DS.Flex>
        </DS.Flex>
      </DS.Flex>
    </DS.DarkModeProvider>
  );
};
